import { useEffect, useRef, useState } from 'react';
import { useMediaViewerListener } from './useMediaViewerListener';

// Add debounce to the scroll handler to ensure that it is executed only once after X ms of delay
const handleScrollDeboubced = <T extends (...args: any[]) => void>(
	scrollHandler: T,
	delay: number,
): ((...args: Parameters<T>) => void) => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	return (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			scrollHandler(...args);
		}, delay);
	};
};

export const useGetCurrentScrollPosition = (isHeightSetFromContent?: boolean): void => {
	const [scrollPosition, setScrollPosition] = useState(0);

	const defaultScrollPosition = useRef(0); // mutable ref to 0 - return this value when the media is open
	const prevScrollPosition = useRef(0);

	const { isMediaOpen } = useMediaViewerListener();

	useEffect(() => {
		const handleScroll = handleScrollDeboubced(() => {
			if (!isMediaOpen) {
				/*
					Saving the previous scroll position is essential because, by the time we receive the state of the media viewer,
					it may already have been opened or closed, and we do not have control over the actions that occur
					just before the media viewer is activated (which happens in a different package that we do not manage).
					Consequently, it is possible for the variable isMediaOpen to be false even when the media viewer is indeed open.
					This situation results in saving of an incorrect value for the scroll position when the media viewer is already active,
					which is misleading since the container height will have been reduced at this point.
					To mitigate this inaccuracy, we continuously track the scroll position when the media viewer is not open.
					When the media is subsequently clicked to open, retrieving the previous scroll position helps to offset the
					inaccuracies associated with saving the scroll position under the conditions of a condensed height.
				*/
				prevScrollPosition.current = scrollPosition;
				setScrollPosition(window.scrollY);
			}
		}, 100); // Call the handler only once scroll event has stopped firing for a delay of 100ms.
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [isMediaOpen, scrollPosition]);

	useEffect(() => {
		if (isHeightSetFromContent) {
			// Restore the scroll position back to the value saved before any media file was open
			window.scrollTo(0, isMediaOpen ? defaultScrollPosition.current : prevScrollPosition.current);
		}
	}, [isHeightSetFromContent, isMediaOpen]);
};
